import { get } from "svelte/store";
import { bodyDimensions, getFirmness } from "./bodyDimensions";
import { questionnaire } from "./questionnaire";
import type {
  Advice,
  AdviceApiResponse,
  AnswerAdvice,
  RawAdvice,
} from "../types/advice";

export function mapToAdvice(rawAdvice: RawAdvice): Advice {
  const mattressSlugs = getMattressSlugs(rawAdvice);
  const mappedAdvice = {};

  mattressSlugs.forEach((mattressSlug, index) =>
    Object.assign(
      mappedAdvice,
      mapMattressToAdvice(mattressSlug, index, rawAdvice)
    )
  );

  return mappedAdvice;
}

export function getMattressType(response: AdviceApiResponse): string {
  return response["mattress-type"][0].value;
}

function getMattressSlugs(rawAdvice: RawAdvice): Array<string> {
  const budgetQuestion = Object.entries(rawAdvice).find(([key, _]) =>
    key.startsWith("budget-")
  )[1];

  return budgetQuestion.value.split("|");
}

function mapMattressToAdvice(
  mattressSlug: string,
  mattressSlugIndex: number,
  rawAdvice: RawAdvice
): Advice {
  const mattressAdvice = Object.entries(rawAdvice).reduce(
    (adviceEntries, [rawQuestionSlug, answer]) => {
      const questionSlug = rawQuestionSlug.split("-partner")[0];

      switch (rawQuestionSlug) {
        case "length-weight":
        case "length-weight-partner":
          Object.assign(adviceEntries, {
            [questionSlug]: getBmiAdvice(mattressSlug, rawQuestionSlug),
          });
          break;
        case "body-type":
        case "body-type-partner":
          Object.assign(adviceEntries, {
            [questionSlug]: getBodyTypeAdvice(mattressSlug, answer),
          });
          break;
        case "budget-one-person":
        case "budget-two-persons":
          Object.assign(adviceEntries, {
            budget: getBudgetAdvice(mattressSlugIndex),
          });
      }

      return adviceEntries;
    },
    {}
  );

  return { [mattressSlug]: mattressAdvice };
}

function getBudgetAdvice(mattressSlugIndex: number): AnswerAdvice {
  const answers = ["best-choice", "sharp-price", "step-extra"];

  const reasons = budgetIsNotUnknown()
    ? [`reason-${answers[mattressSlugIndex]}`]
    : [];

  return {
    value: answers[mattressSlugIndex],
    reasons,
  };
}

function budgetIsNotUnknown(): boolean {
  const budgetQuestion = get(questionnaire).activeQuestions.filter((question) =>
    question.slug.startsWith("budget-")
  );

  return budgetQuestion[0]?.selectedAnswer?.slug !== "unknown";
}

function getBodyTypeAdvice(mattressSlug: string, answer): AnswerAdvice {
  switch (mattressSlug) {
    case "one":
      return {
        value: "3-zones",
        reasons: ["reason-3-zones"],
      };
    case "two":
      return {
        value: "5-zones",
        reasons: ["reason-5-zones"],
      };
    default:
      return {
        value: answer.value,
        reasons: answer.reasons.map((reason) => reason.slug),
      };
  }
}

function getBmiAdvice(
  mattressSlug: string,
  questionSlug: string
): AnswerAdvice {
  const firmness = getFirmness(
    mattressSlug,
    get(bodyDimensions)[questionSlug].bmi
  );

  return {
    value: firmness,
    reasons: [`reason-${firmness}`],
  };
}
